import type {
  Diagram,
  ProjectData,
  AppState,
  ThreatCatNote,
  NoteType,
  ReviewComment,
  ReviewCommentStatus,
  ReviewCommentSource,
  ReviewSeverity,
  ReviewTargetType,
  Handover,
} from '@/types';

export class ParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParseError';
  }
}

export function parseJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new ParseError(`Invalid JSON format: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export function isProjectData(data: unknown): data is ProjectData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'project_version' in data &&
    'diagrams' in data &&
    Array.isArray((data as ProjectData).diagrams)
  );
}

export function isSingleDiagram(data: unknown): data is Diagram {
  return (
    typeof data === 'object' &&
    data !== null &&
    'entities' in data &&
    'boundaries' in data &&
    'paths' in data &&
    Array.isArray((data as Diagram).entities) &&
    Array.isArray((data as Diagram).boundaries) &&
    Array.isArray((data as Diagram).paths)
  );
}

export function normalizeData(rawData: unknown): AppState {
  // Full project export
  if (isProjectData(rawData)) {    const validNoteTypes = new Set<NoteType>(['general', 'assumption', 'scope', 'out_of_scope']);
    const rawNotes = (rawData as unknown as Record<string, unknown>).notes;
    const notes: ThreatCatNote[] = Array.isArray(rawNotes)
      ? (rawNotes as Array<Record<string, unknown>>)
          .filter((n) => validNoteTypes.has(n.type as NoteType))
          .map((n) => ({
            id: typeof n.id === 'number' ? n.id : undefined,
            uuid: typeof n.uuid === 'string' ? n.uuid : undefined,
            type: n.type as NoteType,
            value: typeof n.value === 'string' ? n.value : '',
            diagram_id: typeof n.diagram_id === 'number' ? n.diagram_id : null,
          }))
      : [];

    const rawRecord = rawData as unknown as Record<string, unknown>;
    const rawComments = rawRecord.review_comments;
    const reviewComments: ReviewComment[] = Array.isArray(rawComments)
      ? (rawComments as Array<Record<string, unknown>>)
          .filter((c) => c && typeof c === 'object' && typeof c.id === 'number')
          .map((c) => ({
            id: c.id as number,
            ...(typeof c.project_version_id === 'number' ? { project_version_id: c.project_version_id } : {}),
            description: typeof c.description === 'string' ? c.description : null,
            severity: (typeof c.severity === 'string' ? c.severity : null) as ReviewSeverity | null,
            status: (typeof c.status === 'string' ? c.status : 'suggested') as ReviewCommentStatus,
            created_at: typeof c.created_at === 'string' ? c.created_at : '',
            edited_at: typeof c.edited_at === 'string' ? c.edited_at : null,
            created_by: typeof c.created_by === 'string' ? c.created_by : null,
            target_type: (typeof c.target_type === 'string' ? c.target_type : 'project_version') as ReviewTargetType,
            target_id: typeof c.target_id === 'number' ? c.target_id : 0,
            parent_type: (typeof c.parent_type === 'string' ? c.parent_type : null) as ReviewTargetType | null,
            parent_id: typeof c.parent_id === 'number' ? c.parent_id : null,
            source: (typeof c.source === 'string' ? c.source : 'user') as ReviewCommentSource,
            quality_check_id: typeof c.quality_check_id === 'number' ? c.quality_check_id : null,
            quality_check_type: typeof c.quality_check_type === 'string' ? c.quality_check_type : null,
          }))
      : [];

    const pvRecord = (rawData.project_version as unknown as Record<string, unknown>) || {};
    const rawHandovers = Array.isArray(pvRecord.handovers)
      ? pvRecord.handovers
      : Array.isArray(rawRecord.handovers)
        ? rawRecord.handovers
        : [];
    const handovers: Handover[] = (rawHandovers as Array<Record<string, unknown>>)
      .filter((h) => h && typeof h === 'object' && typeof h.type === 'string')
      .map((h) => ({
        ...(typeof h.id === 'number' ? { id: h.id } : {}),
        ...(typeof h.project_version_id === 'number' ? { project_version_id: h.project_version_id } : {}),
        type: h.type as string,
        created_at: typeof h.created_at === 'string' ? h.created_at : '',
        submitter: typeof h.submitter === 'string' ? h.submitter : null,
        comment: typeof h.comment === 'string' ? h.comment : null,
      }));

    return {
      isProject: true,
      projectInfo: rawData.project_version,
      diagrams: rawData.diagrams,
      threats: rawData.threats || [],
      notes,
      reviewComments,
      handovers,
      currentDiagramIndex: 0,
      currentDiagram: rawData.diagrams[0] ?? null,
    };
  }

  // Single diagram export
  if (isSingleDiagram(rawData)) {
    return {
      isProject: false,
      projectInfo: null,
      diagrams: [rawData],
      threats: [],
      notes: [],
      reviewComments: [],
      handovers: [],
      currentDiagramIndex: 0,
      currentDiagram: rawData,
    };
  }

  throw new ParseError(
    'Invalid JSON format: Expected either a project with diagrams[] or a single diagram with entities[], boundaries[], and paths[]'
  );
}
